import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Construction } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import Logo from "@/components/Logo";

const UnderConstruction = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar" || i18n.language === "ku";

  const [message, setMessage] = useState<string>(t("underConstruction.subtitle"));

  useEffect(() => {
    supabase
      .from("site_settings")
      .select("content")
      .eq("id", "maintenance")
      .maybeSingle()
      .then(({ data }) => {
        const c = (data?.content as any) || {};
        if (c?.message) setMessage(c.message);
      });
  }, []);

  return (
    <div className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-[#0a0a0a] px-6 text-center">
      {/* Animated glowing blobs */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/4 left-1/4 w-72 h-72 rounded-full opacity-40 blur-[100px] animate-blob bg-brand-yellow"
          style={{ animationDelay: "0s" }}
        />
        <div
          className="absolute top-1/2 right-1/4 w-96 h-96 rounded-full opacity-30 blur-[120px] animate-blob bg-brand-teal"
          style={{ animationDelay: "2s" }}
        />
        <div
          className="absolute bottom-1/4 left-1/3 w-80 h-80 rounded-full opacity-25 blur-[110px] animate-blob bg-brand-amber"
          style={{ animationDelay: "4s" }}
        />
      </div>

      {/* Glass card */}
      <div
        className="relative z-10 flex flex-col items-center max-w-xl w-full animate-fade-up"
        dir={isRTL ? "rtl" : "ltr"}
      >
        <div className="mb-8">
          <Logo />
        </div>

        <div className="w-full rounded-2xl border border-white/10 bg-white/5 backdrop-blur-xl p-8 md:p-12 shadow-2xl shadow-brand-yellow/5">
          {/* Icon with glow */}
          <div className="relative mx-auto mb-8 h-24 w-24">
            <div className="absolute inset-0 rounded-2xl bg-brand-yellow/20 animate-glow-pulse blur-xl" />
            <div className="relative h-24 w-24 rounded-2xl bg-brand-yellow/10 border border-brand-yellow/30 flex items-center justify-center">
              <Construction className="h-12 w-12 text-brand-yellow animate-float" />
            </div>
          </div>

          <h1 className="text-3xl md:text-5xl font-bold mb-4 text-white">
            {t("underConstruction.title")}
          </h1>

          <p className="text-gray-300/80 max-w-md mx-auto text-base md:text-lg leading-relaxed mb-8">
            {message}
          </p>

          {/* Decorative line */}
          <div className="w-16 h-1 bg-gradient-to-r from-transparent via-brand-yellow to-transparent mx-auto mb-8 rounded-full opacity-60" />

          {/* Progress dots */}
          <div className="flex items-center justify-center gap-2 mb-8">
            <span className="w-2 h-2 rounded-full bg-brand-yellow animate-pulse" />
            <span className="w-2 h-2 rounded-full bg-brand-yellow/60 animate-pulse" style={{ animationDelay: "0.2s" }} />
            <span className="w-2 h-2 rounded-full bg-brand-yellow/30 animate-pulse" style={{ animationDelay: "0.4s" }} />
          </div>

          <p className="text-xs text-gray-500">
            {t("underConstruction.adminNote")}{" "}
            <a
              className="underline text-brand-yellow hover:text-brand-yellow/80 transition-colors"
              href="/admin/login"
            >
              /admin/login
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default UnderConstruction;
